import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { WORK_CATEGORIES, WORK_MODES, WORK_SCOPE_TAGS } from "../../site/data";

const MAX_IMAGES = 10;
const CAT_LABEL = Object.fromEntries(WORK_CATEGORIES.map((c) => [c.id, c.label]));
const imgUrl = (key) => `/api/works/image/${key}`;

const EMPTY = {
  category: "", title: "", field: "", period: "", summary: "", body: "",
  scope_tags: [], extraTags: "", mode: "", client: "", youtube: "",
  link_url: "", link_label: "", is_public: false, sort_order: 100,
};

function toForm(w) {
  const tags = w.scope_tags || [];
  return {
    category: w.category || "", title: w.title || "", field: w.field || "", period: w.period || "",
    summary: w.summary || "", body: w.body || "",
    scope_tags: tags.filter((t) => WORK_SCOPE_TAGS.includes(t)),
    extraTags: tags.filter((t) => !WORK_SCOPE_TAGS.includes(t)).join(", "),
    mode: w.mode || "", client: w.client || "", youtube: w.youtube || "",
    link_url: w.link_url || "", link_label: w.link_label || "",
    is_public: Boolean(w.is_public), sort_order: w.sort_order ?? 100,
  };
}

function toPayload(f) {
  const extra = f.extraTags.split(",").map((t) => t.trim()).filter(Boolean);
  return {
    category: f.category,
    title: f.title.trim(),
    field: f.field, period: f.period, summary: f.summary, body: f.body,
    // 선택지 순서대로 정렬한 뒤 직접 입력한 태그를 붙인다
    scope_tags: [...WORK_SCOPE_TAGS.filter((t) => f.scope_tags.includes(t)), ...extra],
    mode: f.mode, client: f.client, youtube: f.youtube,
    link_url: f.link_url, link_label: f.link_label,
    is_public: f.is_public,
    sort_order: Number(f.sort_order) || 100,
  };
}

/* 사진 — 첫 번째가 대표(카드) 사진. 저장 전에도 골라 둘 수 있고, 새로 고른 사진은 등록/저장할 때 올린다. */
function PhotoPicker({ saved, pending, setPending, busy, onRemove, onReorder }) {
  const images = (saved && saved.images) || [];
  const room = MAX_IMAGES - images.length - pending.length;
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const urls = pending.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [pending]);

  function pick(e) {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (files.length && room > 0) setPending((prev) => [...prev, ...files.slice(0, room)]);
  }

  function move(i, d) {
    const next = [...images];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onReorder(next);
  }
  function makeFirst(i) {
    const next = [...images];
    const [k] = next.splice(i, 1);
    onReorder([k, ...next]);
  }

  const thumb = { width: 120, height: 90, objectFit: "cover", borderRadius: 8, display: "block" };
  const total = images.length + pending.length;

  return (
    <div className="sstoa-field" style={{ marginTop: 12 }}>
      <label>
        사진 <span className="sstoa-hint">— {total}/{MAX_IMAGES}장 · 첫 번째가 카드 대표 사진(4:3으로 잘려 보임) · 나머지는 '자세히'에서 보임</span>
      </label>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        {images.map((key, i) => (
          <div key={key} style={{ position: "relative" }}>
            <img src={imgUrl(key)} alt="" style={thumb} />
            {i === 0 && <span className="sstoa-tag" style={{ position: "absolute", left: 4, top: 4 }}>대표</span>}
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
              <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => move(i, -1)} disabled={busy || i === 0} aria-label="앞으로">◀</button>
              <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => move(i, 1)} disabled={busy || i === images.length - 1} aria-label="뒤로">▶</button>
              {i > 0 && <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => makeFirst(i)} disabled={busy}>대표</button>}
              <button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => onRemove(key)} disabled={busy} aria-label="사진 삭제">×</button>
            </div>
          </div>
        ))}
        {pending.map((f, i) => (
          <div key={`${f.name}-${f.size}-${f.lastModified}-${i}`} style={{ position: "relative", opacity: 0.85 }}>
            {previews[i] && <img src={previews[i]} alt="" style={{ ...thumb, outline: "2px dashed var(--s-border, #ccc)" }} />}
            {images.length === 0 && i === 0 && (
              <span className="sstoa-tag" style={{ position: "absolute", left: 4, top: 4 }}>대표</span>
            )}
            <div style={{ marginTop: 4 }}>
              <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))} disabled={busy}>
                선택 취소
              </button>
            </div>
          </div>
        ))}
        {total === 0 && <span className="sstoa-hint">아직 사진이 없습니다. 없으면 분류 아이콘이 대신 보입니다.</span>}
      </div>
      {room > 0 ? (
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={pick} disabled={busy} />
      ) : (
        <span className="sstoa-hint">사진이 {MAX_IMAGES}장 가득 찼습니다.</span>
      )}
      <span className="sstoa-hint" style={{ marginTop: 6 }}>
        jpg · png · webp · gif, 장당 20MB 이하. 올리면 위치정보(EXIF)를 지우고 가로 1920px · WebP 로 자동 변환합니다.
        {images.length > 0 && " 이미 올린 사진의 삭제 · 순서 변경은 누르는 즉시 적용됩니다(취소해도 되돌아가지 않음)."}
        {pending.length > 0 && ` 점선 사진 ${pending.length}장은 ${saved ? "저장" : "등록"}할 때 올라갑니다.`}
      </span>
    </div>
  );
}

/* 편집 팝업 — 바깥(어두운 배경)을 눌러도, ESC 를 눌러도 닫히지 않는다.
   닫는 길은 저장 · 취소 · × 버튼뿐이다. 열려 있는 동안 뒤 목록은 스크롤되지 않는다. */
function Modal({ title, onClose, busy, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="sstoa-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="sstoa-modal__box">
        <div className="sstoa-modal__head">
          <h2 className="sstoa-modal__title">{title}</h2>
          <button type="button" className="sstoa-modal__x" onClick={onClose} disabled={busy} aria-label="닫기">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function WorkForm({ initial, onSaved, onClose }) {
  const [form, setForm] = useState(() => (initial ? toForm(initial) : EMPTY));
  const [baseline] = useState(() => JSON.stringify(initial ? toForm(initial) : EMPTY));
  const [saved, setSaved] = useState(initial || null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function apply(w) { setSaved(w); onSaved(w); }

  function toggleTag(t) {
    setForm((f) => ({
      ...f,
      scope_tags: f.scope_tags.includes(t) ? f.scope_tags.filter((x) => x !== t) : [...f.scope_tags, t],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.category) { setError("분류를 선택해 주세요."); return; }
    if (!form.title.trim()) { setError("제목을 입력해 주세요."); return; }
    setBusy(true); setError("");
    let current = saved;
    try {
      const payload = toPayload(form);
      current = current ? await api.post(`/works/${current.id}`, payload) : await api.post("/works", payload);
      apply(current);

      /* 한 장씩 올린다 — 중간에 실패해도 올라간 사진은 남고 남은 것만 다시 시도하면 된다 */
      const queue = [...photos];
      while (queue.length > 0) {
        setProgress(`사진 올리는 중… (${photos.length - queue.length + 1}/${photos.length})`);
        const fd = new FormData();
        fd.append("file", queue[0]);
        current = await api.upload(`/works/${current.id}/images`, fd);
        apply(current);
        queue.shift();
        setPhotos([...queue]);
      }
      setBusy(false);
      setProgress("");
      onClose(); // 저장(사진 업로드 포함)이 모두 끝났을 때만 닫는다
    } catch (err) {
      setError(err.message); // 실패하면 입력한 내용을 그대로 두고 팝업을 유지한다
      setBusy(false);
      setProgress("");
    }
  }

  /* 취소 · × — 저장하지 않은 변경이 있으면 한 번 더 묻는다 */
  function cancel() {
    if (busy) return;
    const dirty = JSON.stringify(form) !== baseline || photos.length > 0;
    if (dirty && !window.confirm("저장하지 않은 변경 내용이 있습니다. 저장하지 않고 닫을까요?")) return;
    onClose();
  }

  async function removeImage(key) {
    if (!window.confirm("이 사진을 삭제할까요?")) return;
    setBusy(true); setError("");
    try { apply(await api.del(`/works/${saved.id}/images?key=${encodeURIComponent(key)}`)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function reorder(keys) {
    setBusy(true); setError("");
    try { apply(await api.post(`/works/${saved.id}/images/order`, { keys })); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      title={saved ? `개발 사례 수정 — ${saved.title}` : "개발 사례 등록"}
      onClose={cancel}
      busy={busy}
    >
    <form className="sstoa-modal__form" onSubmit={submit}>
      <div className="sstoa-modal__body">
      <div className="sstoa-row">
        <div className="sstoa-field">
          <label>분류 <span className="req">*</span></label>
          <select className="sstoa-select" value={form.category} onChange={upd("category")} disabled={busy}>
            <option value="" disabled>선택하세요</option>
            {WORK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div className="sstoa-field" style={{ flex: 3 }}>
          <label>제목 <span className="req">*</span></label>
          <input className="sstoa-input" value={form.title} onChange={upd("title")} disabled={busy} />
        </div>
      </div>

      <div className="sstoa-row" style={{ marginTop: 12 }}>
        <div className="sstoa-field" style={{ flex: 2 }}>
          <label>분야 <span className="sstoa-hint">— 카드 윗줄 (예: 모션 제어 · 다축 네트워크)</span></label>
          <input className="sstoa-input" value={form.field} onChange={upd("field")} disabled={busy} />
        </div>
        <div className="sstoa-field">
          <label>기간</label>
          <input className="sstoa-input" value={form.period} onChange={upd("period")} disabled={busy} placeholder="2026.08 ~ 진행 중" />
        </div>
      </div>

      <div className="sstoa-field" style={{ marginTop: 12 }}>
        <label>한 줄 설명 <span className="sstoa-hint">— 카드에 보이는 문장</span></label>
        <textarea className="sstoa-textarea" rows={2} value={form.summary} onChange={upd("summary")} disabled={busy} />
      </div>

      <div className="sstoa-field" style={{ marginTop: 12 }}>
        <label>상세 내용 <span className="sstoa-hint">— 카드의 '자세히'를 누르면 펼쳐짐 · 줄바꿈 유지</span></label>
        <textarea className="sstoa-textarea" rows={7} value={form.body} onChange={upd("body")} disabled={busy} />
      </div>

      <PhotoPicker
        saved={saved} pending={photos} setPending={setPhotos} busy={busy}
        onRemove={removeImage} onReorder={reorder}
      />

      <div className="sstoa-field" style={{ marginTop: 12 }}>
        <label>담당 범위 태그</label>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
          {WORK_SCOPE_TAGS.map((t) => (
            <label key={t} style={{ display: "inline-flex", gap: 5, alignItems: "center", fontWeight: 400 }}>
              <input type="checkbox" checked={form.scope_tags.includes(t)} onChange={() => toggleTag(t)} disabled={busy} />
              {t}
            </label>
          ))}
        </div>
        <input className="sstoa-input" value={form.extraTags} onChange={upd("extraTags")} disabled={busy} placeholder="그 밖의 태그 — 쉼표로 구분 (예: 앱, 기구 설계)" />
      </div>

      <div className="sstoa-row" style={{ marginTop: 12 }}>
        <div className="sstoa-field">
          <label>진행 방식 태그</label>
          <select className="sstoa-select" value={form.mode} onChange={upd("mode")} disabled={busy}>
            <option value="">표시 안 함</option>
            {WORK_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="sstoa-field">
          <label>고객명 <span className="sstoa-hint">— 비우면 '비공개'</span></label>
          <input className="sstoa-input" value={form.client} onChange={upd("client")} disabled={busy} />
        </div>
        <div className="sstoa-field">
          <label>유튜브 <span className="sstoa-hint">— 주소 또는 영상 ID</span></label>
          <input className="sstoa-input" value={form.youtube} onChange={upd("youtube")} disabled={busy} placeholder="https://youtu.be/…" />
        </div>
      </div>

      <div className="sstoa-row" style={{ marginTop: 12 }}>
        <div className="sstoa-field">
          <label>관련 링크 주소 <span className="sstoa-hint">— 선택 (예: /forum/3)</span></label>
          <input className="sstoa-input" value={form.link_url} onChange={upd("link_url")} disabled={busy} />
        </div>
        <div className="sstoa-field">
          <label>링크 문구</label>
          <input className="sstoa-input" value={form.link_label} onChange={upd("link_label")} disabled={busy} placeholder="포럼에서 내려받기" />
        </div>
      </div>

      <div className="sstoa-row" style={{ marginTop: 12, alignItems: "flex-end" }}>
        <div className="sstoa-field">
          <label>공개 여부</label>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontWeight: 600 }}>
            <input
              type="checkbox" checked={form.is_public} disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))}
            />
            {form.is_public ? "공개 — 홈페이지에 보임" : "비공개 — 홈페이지에 안 보임"}
          </label>
        </div>
        <div className="sstoa-field">
          <label>정렬 순서 <span className="sstoa-hint">— 분류 안에서 작은 수가 앞</span></label>
          <input className="sstoa-input" type="number" value={form.sort_order} onChange={upd("sort_order")} disabled={busy} />
        </div>
      </div>

      </div>

      <div className="sstoa-modal__foot">
        {error && <div className="sstoa-error sstoa-modal__error">{error}</div>}
        {progress && <span className="sstoa-hint">{progress}</span>}
        {saved && saved.is_public && (
          <a className="sstoa-btn sstoa-btn--sm" href={`/works#${saved.slug}`} target="_blank" rel="noopener noreferrer">
            사이트에서 보기
          </a>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="sstoa-btn" onClick={cancel} disabled={busy}>취소</button>
        <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={busy}>
          {busy ? "저장 중…" : saved ? "저장" : "등록"}
        </button>
      </div>
    </form>
    </Modal>
  );
}

export default function WorksAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | work
  const [error, setError] = useState("");

  async function load() {
    setError("");
    try { setRows(await api.get("/works")); }
    catch (err) { setError(err.message); }
  }
  useEffect(() => { load(); }, []);

  async function togglePublic(w) {
    try { await api.post(`/works/${w.id}`, { is_public: !w.is_public }); load(); }
    catch (err) { setError(err.message); }
  }

  async function remove(w) {
    if (!window.confirm(`'${w.title}' 사례를 삭제할까요? 사진도 함께 지워집니다.`)) return;
    try { await api.del(`/works/${w.id}`); setEditing(null); load(); }
    catch (err) { setError(err.message); }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa" className="sstoa-back">← 홈</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">개발 사례 관리</h1>
          <p className="sstoa-sub">
            홈페이지 개발 사례(/works)와 홈 미리보기에 나오는 사례입니다. 공개로 설정한 것만 사이트에 보이고, 저장하면 바로 반영됩니다.
          </p>
        </div>
        <button className="sstoa-btn sstoa-btn--primary" onClick={() => setEditing("new")}>+ 사례 등록</button>
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      {editing && (
        <WorkForm
          key={editing === "new" ? "new" : editing.id}
          initial={editing === "new" ? null : editing}
          onSaved={() => load()}
          onClose={() => { setEditing(null); load(); }}
        />
      )}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">등록된 사례가 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr><th>공개</th><th>분류</th><th></th><th>제목</th><th>사진</th><th>순서</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.id}>
                  <td>
                    <button
                      type="button"
                      className={`sstoa-btn sstoa-btn--sm${w.is_public ? " sstoa-btn--primary" : ""}`}
                      onClick={() => togglePublic(w)}
                      title="눌러서 공개 / 비공개 전환"
                    >
                      {w.is_public ? "공개" : "비공개"}
                    </button>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{CAT_LABEL[w.category] || w.category}</td>
                  <td style={{ width: 64 }}>
                    {(w.images || [])[0] ? (
                      <img src={imgUrl(w.images[0])} alt="" style={{ width: 56, height: 42, objectFit: "cover", borderRadius: 6, display: "block" }} />
                    ) : null}
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {w.title}
                    <div style={{ fontWeight: 400, fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>
                      {w.field || "—"}
                    </div>
                  </td>
                  <td>{(w.images || []).length}장</td>
                  <td>{w.sort_order}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="sstoa-btn sstoa-btn--sm" onClick={() => setEditing(w)}>수정</button>{" "}
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(w)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
